import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ErrorState,
  Input,
  Label,
  Skeleton,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { fetchOrgSettings, patchOrgSettings } from '../../../api/org-settings.js';

/**
 * `/settings/ai` — admin-only. Toggles for the master AI switch, the
 * four feature flags, the auto-apply classification threshold, and the
 * default Whisper language. The AI usage dashboard lives on the
 * super-admin side; this page is the org's own self-serve controls.
 */
export function AiSettingsPage() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['org-settings'],
    queryFn: fetchOrgSettings,
  });

  const [enabled, setEnabled] = useState(true);
  const [features, setFeatures] = useState({
    emailParse: true,
    woClassify: true,
    violationSummarize: true,
    voiceToWo: true,
  });
  const [autoApply, setAutoApply] = useState(false);
  const [autoApplyThreshold, setAutoApplyThreshold] = useState(0.9);
  const [language, setLanguage] = useState('en');
  const [debug, setDebug] = useState(false);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const ai = settingsQuery.data.settings.ai;
    setEnabled(ai.enabled);
    setFeatures({ ...ai.features });
    setAutoApply(ai.autoApplyClassification);
    setAutoApplyThreshold(ai.autoApplyClassificationThreshold);
    setLanguage(ai.defaultTranscriptionLanguage);
    setDebug(ai.aiDebugMode);
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      patchOrgSettings({
        ai: {
          enabled,
          features,
          autoApplyClassification: autoApply,
          autoApplyClassificationThreshold: autoApplyThreshold,
          defaultTranscriptionLanguage: language,
          aiDebugMode: debug,
        },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-settings'] }),
  });

  if (settingsQuery.isError) {
    return <ErrorState description="Could not load settings." />;
  }
  if (settingsQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Master switch</h2>
          <p className="text-xs text-fg-muted">
            When off, every AI feature is bypassed and SiteWise uses the non-AI fallbacks.
            Per-feature toggles below take effect only while this is on.
          </p>
        </CardHeader>
        <CardContent>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Enable AI features for this org
          </label>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Per-feature toggles</h2>
          <p className="text-xs text-fg-muted">
            Disable individual features without turning AI off entirely. Disabled
            features still log a `feature_disabled` row in the audit trail.
          </p>
        </CardHeader>
        <CardContent className="space-y-2">
          <Toggle
            label="Vendor email parsing"
            checked={features.emailParse}
            onChange={(v) => setFeatures({ ...features, emailParse: v })}
            disabled={!enabled}
          />
          <Toggle
            label="Work order classification (auto-suggest category & severity)"
            checked={features.woClassify}
            onChange={(v) => setFeatures({ ...features, woClassify: v })}
            disabled={!enabled}
          />
          <Toggle
            label="Violation summarization (plain-English gloss)"
            checked={features.violationSummarize}
            onChange={(v) => setFeatures({ ...features, violationSummarize: v })}
            disabled={!enabled}
          />
          <Toggle
            label="Voice → work order (Whisper transcription + structuring)"
            checked={features.voiceToWo}
            onChange={(v) => setFeatures({ ...features, voiceToWo: v })}
            disabled={!enabled}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Auto-apply classification</h2>
          <p className="text-xs text-fg-muted">
            When confidence ≥ threshold, the classifier applies its own suggestion to
            the work order without waiting for human review. Even when auto-applied,
            users can still change category / severity afterward.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <Toggle
            label="Auto-apply when confidence is high enough"
            checked={autoApply}
            onChange={setAutoApply}
            disabled={!enabled || !features.woClassify}
          />
          <div className="flex items-center gap-2">
            <Label className="text-sm">Threshold</Label>
            <Input
              type="number"
              min={0.5}
              max={1}
              step={0.05}
              value={autoApplyThreshold}
              onChange={(e) => setAutoApplyThreshold(Number(e.target.value))}
              className="w-24"
              disabled={!autoApply}
            />
            <span className="text-xs text-fg-muted">
              0.5 – 1.0 (default 0.9)
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Voice transcription</h2>
          <p className="text-xs text-fg-muted">
            ISO 639-1 hint passed to Whisper. Whisper auto-detects language too,
            but a hint speeds up non-English memos noticeably.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Language</Label>
            <Input
              type="text"
              value={language}
              onChange={(e) => setLanguage(e.target.value.trim())}
              maxLength={8}
              className="w-20"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="text-sm font-semibold">Debug mode</h2>
          <p className="text-xs text-fg-muted">
            When on, the runner stores the full prompts (including any PII in the source
            content) in <code>ai_calls.metadata.fullPrompt</code> for troubleshooting.
            Off by default — leave off unless actively debugging.
          </p>
        </CardHeader>
        <CardContent>
          <Toggle
            label="Persist full prompts in ai_calls (debug only)"
            checked={debug}
            onChange={setDebug}
          />
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        {saveMutation.isError ? (
          <span className="text-xs text-danger">Could not save settings.</span>
        ) : saveMutation.isSuccess ? (
          <span className="text-xs text-success">Saved.</span>
        ) : null}
      </div>
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50' : ''}`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      {label}
    </label>
  );
}
